import { updateCartLines } from '@theme/cart-actions';

/**
 * Product card behaviour — specs 032 (gallery), 033 (size grid + quick-add),
 * 036 (video). One module for all three, because they share the same intent
 * contract and the same budget.
 *
 * ⚠️ THIS FILE HAS ONE IMPORT SINCE 2026-09-08 (U3), and both this block and
 * `snippets/scripts.liquid` used to say it had none. `@theme/cart-actions` is the
 * theme's single cart-mutation chokepoint, and the quick-add listener at the bottom
 * of this file was the one call site bypassing it — see the U3 note there for what
 * that cost. The import adds no bytes to any page that renders cards: `cart-actions.js`
 * is already in the graph from `component-cart-items.js`, which
 * `snippets/cart-products.liquid` loads for the drawer that `layout/theme.liquid`
 * renders on every template. MEASURED on a rendered collection page with
 * `performance.getEntriesByType('resource')` before the import was added.
 *
 * ARCHITECTURE — the part that matters at 40 cards per page:
 *   - ZERO per-card listeners. SIX delegated listeners on `document`, total,
 *     for the whole grid (:404 pointerover, :416 focusin, :421 click,
 *     :503 scroll (capture, swipe dots — added 2026-09-29), :515 keydown,
 *     :671 submit). Not 40 of anything. Plus ONE lifecycle listener that
 *     delegates nothing (:752 visibilitychange), added in U4 for 044 §4 rule 5
 *     — counted here so the "six" above stays exactly true.
 *
 *     ⚠️ Line numbers re-read at HEAD 2026-09-29 by
 *     `grep -n addEventListener assets/rp-card.js`. They had drifted by ~100
 *     lines and the COUNT had drifted too: U2's autoplay commit added a SECOND
 *     delegated `click` listener for the pause latch, making six, while this
 *     block still said five. That listener is gone — the latch moved onto the
 *     media element, where it can actually observe a native-control pause — so
 *     five is true again rather than merely still written down.
 *
 *   - TWO listeners exist on the mounted <video> (:343 pause, :360 play). They
 *     are not per-card and not delegated: there is at most ONE mounted video on
 *     the page, they are created with it, and `stopVideo` aborts their
 *     AbortController before teardown. Named here because "ZERO per-card
 *     listeners" above is about the grid, and silence about these two is how a
 *     count starts drifting again.
 *   - ONE IntersectionObserver for the whole page (:855). motion.js owns the
 *     other (motion.js:40); spec 060 §6.3 caps the page at 3.
 *
 *     ⚠️ THIS BULLET WAS CORRUPTED AND IS REBUILT. Until 2026-09-07 it carried
 *     TWO half-sentences spliced together — "ONE IntersectionObserver for the
 *     whole page (:521). motion.js owns the" ran straight into "for the whole
 *     grid (:404 pointerover, …)" — the U10 correction of 2026-09-04 having been
 *     pasted in without removing the text it replaced. So it stated the listener
 *     lines twice, with two different sets of numbers (:278/:290/:295/:340/:466
 *     and :254/:266/:271/:311/:396) and the observer twice (:521 and :469).
 *     Every number in all three sets was stale. Re-read at HEAD by
 *     `grep -n addEventListener assets/rp-card.js` and
 *     `grep -n 'new IntersectionObserver' assets/rp-card.js`; the six lines above
 *     are that grep's output. The COUNTS were and remain right — five delegated
 *     plus one lifecycle, one observer. Deleting the size trigger removed no
 *     listener: Space on it was handled inside the keydown listener that already
 *     existed, which is why the count did not move when the trigger went.
 *   - ONE scroll listener (the swipe-dots capture above; 060 §6.3's site-wide
 *     cap is 1 and it was unspent) and ZERO resize listeners. Both caps come
 *     from spec 060 §6.3, NOT from AGENTS.md, which says nothing about listeners. (U10
 *     fixed three wrong claims here on 2026-09-03 — "six" listeners, the
 *     AGENTS.md attribution, and "the motion layer has [the scroll listener]".
 *     Evidence in docs/BUILD.md, U10 register.)
 *   - `will-change` is never set, on anything. Forty promoted layers to fade a
 *     hairline would cost more GPU memory than every other card decision
 *     combined (035 §4).
 *
 * NO USER-FACING STRINGS LIVE HERE. Every label is rendered by Liquid — dots
 * and arrows arrive pre-labelled inside <template data-rp-controls>, deferred
 * image `alt` travels in the JSON payload, and the quick-add error line is
 * markup that is unhidden rather than text that is written. This is Horizon's
 * own convention (`snippets/slideshow-controls.liquid:112,166` render
 * `accessibility.slide_status` in Liquid); the `data-*-template` contract the
 * specs describe belongs to a dead theme and does not exist in the donor.
 *
 * NO prefers-reduced-motion DETECTION HERE EITHER. Ours happens in two places —
 * the inline script in layout/theme.liquid:34 and assets/motion.js:29 — and those
 * are the only two we own. ⚠️ Corrected 2026-09-04: this used to say "exactly two
 * places", which a grep disproves. Grepping assets/ and layout/ for a matchMedia
 * call on the reduce query returns THREE call sites, not two: theme.liquid:34,
 * motion.js:29 and — on every page — Horizon's assets/utilities.js:254, which is
 * modulepreloaded from snippets/scripts.liquid:73. (Deliberately not writing the
 * query string out: a grep for it would then match this comment, which is how
 * BUILD.md's stylesheet-tag count once read 156.) The third one is inherited
 * debt, tracked in docs/BUILD.md's targets table, not a fourth thing to write.
 *
 * ⭐ THE RULE THIS FILE USED TO STATE IS WITHDRAWN. It said: "This module starts
 * nothing on its own, so it has nothing to gate: the only thing that ever plays
 * is a video the shopper explicitly activated." That was a deliberate, defensible
 * default and the CLIENT WITHDREW IT on 2026-09-09 — *"Video should auto play,
 * muted. Change that as a decision."* Recorded as `docs/decisions/008-video-autoplay.md`,
 * which is the authority; this comment must not drift from it.
 *
 * What replaces it: on a pointer device, the card's video plays by itself, muted
 * and inline, WHEN ITS OWN SLIDE IS THE ONE ON SCREEN and the card is in the
 * viewport. It is still one video at a time, it still mounts nothing under
 * reduced motion, and it still mounts nothing where there is no hover. See the
 * 036b block near the bottom of this file for the mechanism and ADR-008 for why
 * the merchandised slide order is left alone.
 */

/** Pointer devices get the layer stepper; everything else gets a native
 *  scroll-snap swipe track (until 2026-09-29 it UNSTACKED into a vertical run
 *  of images, on a misreading — see src/u8-product-card.css). Evaluated once — no resize
 *  listener, and no duplicated media query in CSS. The controller writes
 *  [data-layered] or [data-swipe] and the stylesheet keys off that. */
const layered = matchMedia('(hover: hover) and (pointer: fine)').matches;

/** The one player allowed at a time. Concurrent hardware decoders are a
 *  device resource (2–4 on mid-range Android) and exceeding it drops the page
 *  to software decode — 036 §4. */
let openVideo = null;

/* ⭐ 2026-09-07 — `openPanel` DELETED, with `upgradeTrigger`, `openSizes` and
 * `closeSizes`. `docs/canvas/Collection.dc.html` has no trigger and no open
 * state: its whole quick-add interaction is `.card:hover .sizes { opacity:1;
 * transform:translateY(0) }`. A panel that CSS reveals has nothing for a
 * controller to open, close, or keep track of as "the one open panel", so the
 * state, the two functions and the two branches that read them are gone rather
 * than left inert. 033 §5 argued for the disclosure; the artboard outranks it
 * (docs/PROCESS.md §4g) and §5 has been corrected in the same commit.
 *
 * Net: this module drops from five delegated listeners to five (the click
 * listener survives for dots and arrows) and from ~60 lines of disclosure
 * bookkeeping to none. The Escape key no longer closes a size panel because
 * there is no longer a panel that opens. */

const gallery = (el) => el?.closest('[data-rp-gallery]') ?? null;

/** The gallery of the CARD an event landed in. Intent listeners need this, not
 *  `gallery()`: `.rp-card__title-link::after` stretches over the whole card
 *  (src/app.css), so a pointer or focus on the image lands on the link, which
 *  is outside the gallery. Until 2026-09-29 hover therefore never hydrated on
 *  desktop — no arrows, no bars — and nothing said so. */
const cardGallery = (el) => el?.closest('.rp-card')?.querySelector('[data-rp-gallery]') ?? null;

/* ── 032 · gallery hydration ─────────────────────────────────────────────── */

/**
 * Materialise slides 2–N from the compact payload.
 *
 * The payload is one CDN base URL plus a Liquid-rendered `alt` per image — no
 * `srcset` text in the document. That is the difference between ~120 bytes and
 * ~550 bytes per deferred image; across 40 cards it is the difference between
 * ~19 KB and ~88 KB of markup (032 §4). The candidate list is declared once, in
 * `data-widths`, so it does not live in two places.
 */
function hydrate(gal) {
  if (gal.dataset.hydrated !== undefined) return;
  gal.dataset.hydrated = '';

  // `gal` IS the media box: [data-rp-gallery] sits on .rp-card__media.
  gal.dataset[layered ? 'layered' : 'swipe'] = '';

  let payload = [];
  try {
    payload = JSON.parse(gal.dataset.slides || '[]');
  } catch {
    payload = [];
  }

  const widths = (gal.dataset.widths || '360').split(',');
  const imgSizes = gal.dataset.imgSizes || '';
  const host = gal.querySelector('[data-rp-slides]');
  if (!host) return;

  const frag = document.createDocumentFragment();

  for (const item of payload) {
    if (!item || !item.u) continue;
    const join = item.u.includes('?') ? '&' : '?';
    const img = document.createElement('img');
    img.className = 'rp-card__img';
    img.srcset = widths.map((w) => `${item.u}${join}width=${w} ${w}w`).join(', ');
    img.src = `${item.u}${join}width=${widths[widths.length - 1]}`;
    if (imgSizes) img.sizes = imgSizes;
    img.alt = item.a || '';
    // Deferred slides never carry fetchpriority — the LCP element is card #1's
    // first image and nothing here may compete with it (032 §4).
    img.loading = 'lazy';
    img.decoding = 'async';

    const slide = document.createElement('div');
    slide.className = 'rp-card__slide';
    // `f` is the per-slide fit the Liquid decided from the image's own aspect
    // ratio (see the cover/contain comment in snippets/repose-card.liquid). It
    // lands on the SLIDE, not the image, because a contained slide is a padded
    // ground box — the same shape .rp-card__frame[data-fit] makes for frame 1.
    slide.dataset.fit = item.f === 'contain' ? 'contain' : 'cover';
    slide.appendChild(img);
    frag.appendChild(slide);
  }

  // The video slide and, in layered mode, the dots and arrows are Liquid-
  // rendered inside <template data-rp-controls> so their labels are translated
  // at render time. Cloning is the whole of the work.
  const controls = gal.querySelector('template[data-rp-controls]');
  if (controls) {
    const clone = controls.content.cloneNode(true);
    if (!layered) {
      // Swipe is the navigation on touch, so no arrows. The dots stay: they are
      // the only sign there is more than one image.
      clone.querySelectorAll('[data-rp-arrow]').forEach((n) => n.remove());
    }
    frag.appendChild(clone);
  }

  host.appendChild(frag);

  // Re-home the stepper affordances out of the slide host. Layered: onto the
  // first frame, which the layers cover. Swipe: onto the media box, because the
  // first frame scrolls away with the track and would take the dots with it.
  const home = layered ? gal.querySelector('[data-rp-frame]') : gal;
  if (home) {
    host.querySelectorAll('[data-rp-dots], [data-rp-arrow]').forEach((n) => home.appendChild(n));
  }

  if (layered) show(gal, 0);
}

/** Strip a hydrated card back to its payload. Without this a long scroll
 *  session accumulates decoded images for every card it passed (032 §4). */
function release(gal) {
  if (gal.dataset.hydrated === undefined) return;
  if (openVideo && gal.contains(openVideo)) return;

  const host = gal.querySelector('[data-rp-slides]');
  if (host) host.replaceChildren();
  gal.querySelectorAll('[data-rp-dots], [data-rp-arrow]').forEach((n) => n.remove());

  delete gal.dataset.layered;
  delete gal.dataset.swipe;
  delete gal.dataset.hydrated;
  delete gal.dataset.index;
}

/** Layered mode only. Index 0 is the first frame itself, which never fades —
 *  the 031 label and the 033 controls live inside it and must stay visible — so
 *  "index 0" simply means no layer is showing. */
function show(gal, index) {
  const slides = gal.querySelectorAll('[data-rp-slides] .rp-card__slide');
  const total = slides.length + 1;
  const next = ((index % total) + total) % total;
  gal.dataset.index = String(next);

  slides.forEach((slide, i) => {
    if (i + 1 === next) slide.dataset.active = '';
    else delete slide.dataset.active;
  });

  gal.querySelectorAll('[data-rp-dot]').forEach((dot, i) => {
    dot.setAttribute('aria-current', i === next ? 'true' : 'false');
  });

  // Landing ON the video slide starts it; leaving it stops it. This lives here,
  // in the ONE function that changes which slide is shown, rather than in
  // `step()` — a dot click calls `show()` directly and never goes through
  // `step()`, so a dot jump to the video slide used to do nothing at all.
  autoplayIfShown(gal);
}

const step = (gal, delta) => show(gal, Number(gal.dataset.index || 0) + delta);

/* ── 033 · size panel ────────────────────────────────────────────────────── */

/* ⭐ NOTHING. The panel is opened by `.rp-card:hover .rp-card__sizes` and
 * `.rp-card:focus-within .rp-card__sizes` in `src/u8-product-card.css`, lifted
 * from `docs/canvas/Collection.dc.html`'s own style block. The only JavaScript
 * the panel still needs is the quick-add submit handler below, which is about
 * the CART, not about the panel.
 *
 * The form is authored in the document now rather than inside
 * `<template data-rp-sizes>`, so it also submits with no script at all — the
 * no-JS route to a purchase that the deleted trigger's `<a href>` used to be. */

/* ── 036 · video ─────────────────────────────────────────────────────────── */

/** Per-mount listener scope. Teardown aborts it BEFORE dropping the source,
 *  because the media load algorithm fires its own `pause` on the way out and
 *  that pause must never reach the user-pause latch. */
const mounted = new WeakMap();

/**
 * One place writes the control's state, so the glyph and the accessible name
 * cannot disagree. Same contract as `assets/rp-pdp-video.js`'s `mark()`: the
 * labels are read off `data-*` attributes the Liquid filled through `t`, so this
 * module keeps its zero user-facing strings.
 *
 * ⚠️ The button is NO LONGER HIDDEN while the clip plays, and that is a fix, not
 * a style change. `playVideo` used to set `button.hidden = true`, which made the
 * click-latch below unreachable — a hidden button takes no clicks — so a
 * shopper's pause could not stick even though a listener existed to catch it.
 * With autoplay on it is also a WCAG 2.2.2 requirement: motion the shopper did
 * not start needs a persistent, never-hover-revealed pause control.
 */
function mark(host, playing) {
  const button = host.querySelector('[data-rp-play]');
  if (!button) return;
  button.toggleAttribute('data-playing', playing);
  const label = playing ? button.dataset.labelPause : button.dataset.labelPlay;
  if (label) button.setAttribute('aria-label', label);
}

function playVideo(button) {
  const host = button.closest('[data-rp-video]');
  if (!host) return;
  // Toggling the host that is already open must not tear it down and rebuild it
  // — that would refetch. Only a DIFFERENT host displaces the open one.
  if (openVideo && openVideo !== host) stopVideo();

  let video = host.querySelector('video');
  if (!video) {
    const tpl = host.querySelector('template[data-rp-player]');
    if (!tpl) return;
    host.appendChild(tpl.content.cloneNode(true));
    video = host.querySelector('video');
    if (!video) return;

    // `video_tag` DOES emit `playsinline` — measured in the served HTML, see
    // docs/BUILD.md — but this is idempotent and it is the attribute autoplay
    // hard-depends on, so it is asserted anyway. `muted` likewise: it is the
    // precondition for the play() promise resolving at all (WCAG 1.4.2).
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.muted = true;

    const scope = new AbortController();
    mounted.set(host, scope);

    /* ⛔ THE LATCH LIVES HERE NOW, AND THE PREVIOUS TWO ATTEMPTS BOTH MISSED.
     * Attempt 1 listened for `pause` and latched on every one of them, including
     * the programmatic pause during mount, so autoplay was dead on a clip nobody
     * had touched. Attempt 2 (909a6d0) moved the latch to a click on
     * `[data-rp-play]` — correct instinct, unreachable in practice, because
     * `playVideo` hid that button whenever the clip was playing AND the Liquid
     * renders the player with `controls: true`, so the shopper's real pause is a
     * NATIVE control this module never sees a click on.
     *
     * What is actually being detected is "a pause this module did not ask for".
     * So mark the ones it does ask for and treat the rest as the shopper. The
     * flag is an attribute rather than a closure variable because media events
     * are QUEUED, not dispatched synchronously — a `let quiet = true; pause();
     * quiet = false` would always have reset before the event arrived. */
    video.addEventListener(
      'pause',
      () => {
        if (host.dataset.rpQuiet !== undefined) delete host.dataset.rpQuiet;
        /* ⚠️ ON THE GALLERY, NOT ON `host`. `host` is the slide, and `release()`
           clears the slide container with `replaceChildren()` — so a latch stored
           there is destroyed the moment the card scrolls out, and the clip
           autoplays again on the way back. Measured before this change: pause →
           latch '1'; scroll to bottom → latch null; scroll back → playing at
           t=3.95. The gallery element survives `release()` (it only deletes its
           own `hydrated`/`index`/`layered`/`swipe` keys), so the decision
           outlives the unmount, which is what the ADR promises. */
        else gallery(host)?.setAttribute('data-rp-user-paused', '1');
        mark(host, false);
      },
      { signal: scope.signal }
    );
    video.addEventListener('play', () => mark(host, true), { signal: scope.signal });
  }

  openVideo = host;
  // No focus() here. Autoplay must never move focus — it is not a gesture, and
  // stealing focus on scroll would be a worse bug than the one being fixed.
  video.play?.().then(
    () => mark(host, true),
    // Refused (iOS Low Power Mode, Data Saver, a user setting). 044 §2:
    // "autoplay can never be assumed to have happened". Poster and control stay.
    () => mark(host, false)
  );
}

/** pause → drop the source → load() → remove. Detached <video> nodes holding
 *  buffered data are how a long PLP session ends up at hundreds of MB. */
function stopVideo() {
  const host = openVideo;
  if (!host) return;
  openVideo = null;

  // Abort FIRST. `load()` below fires a `pause` of its own, and if the latch
  // listener were still attached every scroll-away would record a user pause and
  // permanently disable autoplay for a card nobody interacted with — the same
  // failure as the original `pause`-event latch, arriving by a different door.
  mounted.get(host)?.abort();
  mounted.delete(host);

  const video = host.querySelector('video');
  if (video) {
    video.pause?.();
    video.removeAttribute('src');
    video.querySelectorAll('source').forEach((s) => s.remove());
    video.load?.();
    video.remove();
  }
  delete host.dataset.rpQuiet;
  mark(host, false);
}

/* ── delegated listeners — six, for the whole page ───────────────────────── */

// Intent on pointer devices. `pointerover` bubbles; `pointerenter` does not, so
// this is one listener rather than one per card.
document.addEventListener(
  'pointerover',
  (event) => {
    if (!layered) return;
    const gal = cardGallery(event.target instanceof Element ? event.target : null);
    if (gal) hydrate(gal);
  },
  { passive: true }
);

// Intent for keyboard users: focus entering the card reaches the same gallery a
// mouse user does (032 §5).
document.addEventListener('focusin', (event) => {
  const gal = cardGallery(event.target instanceof Element ? event.target : null);
  if (gal) hydrate(gal);
});

document.addEventListener('click', (event) => {
  const el = event.target instanceof Element ? event.target : null;
  if (!el) return;

  // Two states of one button — play and pause — mirroring `data-rp-toggle` on
  // the PDP. A click is the shopper asking, so it ignores the autoplay gates:
  // 044 §5 requires the opt-in path to survive reduced motion, and gating the
  // click would remove it.
  const play = el.closest('[data-rp-play]');
  if (play) {
    event.preventDefault();
    const host = play.closest('[data-rp-video]');
    const video = host?.querySelector('video');
    if (video && !video.paused) {
      // Stopping it. The `pause` listener latches `data-rp-user-paused` because
      // this pause is not marked quiet — that is the whole latch.
      video.pause();
    } else {
      // Asking for it clears any previous latch, on this gallery only.
      gallery(host)?.removeAttribute('data-rp-user-paused');
      playVideo(play);
    }
    return;
  }

  const arrow = el.closest('[data-rp-arrow]');
  if (arrow) {
    event.preventDefault();
    const gal = gallery(arrow);
    if (gal) step(gal, arrow.dataset.rpArrow === 'prev' ? -1 : 1);
    return;
  }

  const dot = el.closest('[data-rp-dot]');
  if (dot) {
    event.preventDefault();
    const gal = gallery(dot);
    if (!gal) return;
    const index = Number(dot.dataset.rpDot || 0);
    const track = gal.dataset.swipe !== undefined && gal.querySelector('[data-rp-track]');
    if (track) {
      // The scroll listener below moves `aria-current` when the track settles.
      const instant = document.documentElement.dataset.motion === 'off';
      track.scrollTo({ left: index * track.clientWidth, behavior: instant ? 'auto' : 'smooth' });
    } else {
      show(gal, index);
    }
    return;
  }

  // Swipe mode lifts the track over the card's stretched title link so a pan
  // can reach it (src/u8-product-card.css). A tap on an image is a click — none
  // fires after a pan — and goes to that real link, so its own navigation runs.
  const track = el.closest('[data-swipe] [data-rp-track]');
  if (track && !el.closest('a, button, input, label, select, textarea, video')) {
    track.closest('.rp-card')?.querySelector('.rp-card__title-link')?.click();
    return;
  }

  // No size-trigger branch and no outside-click close: there is no disclosure
  // to toggle or dismiss. Hover and focus own the panel entirely.
});

/* Swipe mode: the bars follow the track. ONE passive capture listener for the
 * whole page — `scroll` does not bubble, so capture is how a delegate hears an
 * element scroll — and at most one sync per track per frame. Page scrolls reach
 * it too (target `document`) and leave at the first check. This is the one
 * `scroll` listener 060 §6.3 allows site-wide; it was unspent. */
const syncing = new Set();

function syncDots(track) {
  syncing.delete(track);
  const gal = gallery(track);
  if (!gal || gal.dataset.swipe === undefined || !track.clientWidth) return;
  const index = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  if (gal.dataset.index === String(index)) return;
  gal.dataset.index = String(index);
  gal.querySelectorAll('[data-rp-dot]').forEach((dot, i) => {
    dot.setAttribute('aria-current', i === index ? 'true' : 'false');
  });
}

document.addEventListener(
  'scroll',
  (event) => {
    const track = event.target;
    if (!(track instanceof Element) || !track.matches('[data-rp-track]')) return;
    if (syncing.has(track)) return;
    syncing.add(track);
    requestAnimationFrame(() => syncDots(track));
  },
  { capture: true, passive: true }
);

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    if (openVideo) {
      const button = openVideo.querySelector('[data-rp-play]');
      stopVideo();
      button?.focus();
      return;
    }
    return;
  }

  // The Space branch that lived here belonged to the deleted trigger: it existed
  // only because an <a> upgraded to role="button" has to answer Space like a
  // button. The size buttons are real <button type="submit">s, which already do.

  if (!layered) return;
  if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

  const el = event.target instanceof Element ? event.target : null;
  const arrows = el?.closest('[data-rp-arrow], [data-rp-dot]');
  if (!arrows) return;
  const gal = gallery(arrows);
  if (!gal) return;
  event.preventDefault();
  step(gal, event.key === 'ArrowLeft' ? -1 : 1);
});

/* Quick-add error = a live region that actually announces. SC 4.1.3 (AA).
 * U4 accessibility audit — full reasoning in docs/reviews/004.
 *
 * The <p role="status" hidden> already holds its translated text, so the old
 * `error.removeAttribute('hidden')` announced NOTHING: `hidden` is display:none,
 * so the region was outside the a11y tree and there was no mutation to observe.
 * Sighted shoppers saw the error; screen-reader shoppers got silence, and
 * role="status" takes no focus, so there was no second channel.
 *
 * Fix: put the EMPTY region in the tree, let frames pass, then write the text —
 * a mutation of an observed region. Two frames, so the empty region is in the
 * tree before the text lands; this runs at
 * most once per failed add. The message is read back from the DOM, never
 * hardcoded, so this module keeps its zero user-facing strings.
 */
/* Cache-or-read, never cache-then-blank: the message must survive being read
 * back after we blank the node, and must not be lost if this runs first on an
 * already-empty one. Returns '' only if there was never any text. */
function addErrorMessage(error) {
  const cached = error.dataset.rpMsg;
  if (cached) return cached;
  const msg = error.textContent.trim();
  if (msg) error.dataset.rpMsg = msg;
  return msg;
}

function resetAddError(error) {
  if (!error) return;
  addErrorMessage(error);
  error.hidden = true;
  error.textContent = '';
}

/**
 * @param {HTMLElement | null} error - The `[data-rp-add-error]` live region.
 * @param {string} [override] - A more specific message than the region's own copy.
 *   Used for a REFUSED add: `updateCartLines` reports "Sold out — not added to your
 *   cart", which tells the shopper what happened, where the region's generic "Error
 *   adding to cart" would suggest a technical fault and leave them re-clicking a size
 *   that is gone. MEASURED 2026-09-08: without this, a capped variant showed the
 *   generic string.
 *
 *   ⚠️ This does NOT put a user-facing string in this module — the override travels
 *   from `Theme.translations`, which `snippets/scripts.liquid` renders through the `t`
 *   filter. The "zero user-facing strings" property at the top of this file holds.
 */
function showAddError(error, override) {
  if (!error) return;
  const msg = override?.trim() || addErrorMessage(error);
  if (!msg) {
    // Nothing to announce and nothing to show. Leave the node exactly as it is
    // rather than unhiding an empty paragraph.
    return;
  }
  error.textContent = '';
  error.hidden = false;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      error.textContent = msg;
    })
  );
}

/**
 * Quick-add. Standard Actions per AGENTS.md rule 4 — never a hand-rolled
 * /cart/add.js.
 *
 * The markup underneath is a real <form method="post"> whose submit buttons carry
 * the variant id, so a missing, rejecting or throwing `Shopify.actions` still adds
 * to cart: the browser performs an ordinary POST. All three were driven in a browser
 * on 2026-09-04 against the rendered card, and each posted the size that was clicked.
 * That is why a wrong guess about the Standard Actions payload cannot break adding
 * to cart.
 *
 * ⚠️ The throwing case was NOT true before 2026-09-04, and the comment here said it
 * was. `preventDefault()` runs first; a SYNCHRONOUS throw out of `updateCart` then
 * escaped this listener before any `.catch` existed, so nothing was posted, no error
 * line appeared, and the button kept `aria-disabled="true"` — a permanently dead
 * control, silent. Measured by serving THIS FILE AS IT STOOD AT 75766a1 next to a
 * `updateCart: () => { throw new Error('boom') }` stub: the page logged
 * `Uncaught Error: boom`, the server logged no request at all, the error line stayed
 * hidden and the button kept `aria-disabled`. `Promise.resolve().then(…)` below is
 * what makes the claim true: it converts a sync throw into a rejection the existing
 * `.catch` handles.
 *
 * The same wrapper covers `updateCart` returning a NON-PROMISE. Same before/after
 * rig, stub `updateCart: () => undefined`: the old code logged
 * `Uncaught TypeError: Cannot read properties of undefined (reading 'then')` and left
 * the identical dead button; the new code treats the resolved-with-undefined action
 * as a success — panel closed, `openCart()` fired, `aria-disabled` cleared. That is
 * the right reading of an action that returns nothing: it did not report an error.
 *
 * ⭐ 2026-09-07 — this DOES now extend to "with JS off". The paragraph that stood
 * here said the form was authored inside `<template data-rp-sizes>` and so was
 * inert without script. True until the card was rebuilt from
 * `docs/canvas/Collection.dc.html`: the canvas reveals the panel with CSS, a
 * <template> cannot be revealed by CSS, so the form is authored in the document
 * and is focusable and submittable with no script at all. The no-JS route to a
 * purchase is now this form, not a link to the PDP.
 *
 * ── ⭐ 2026-09-08, U3 — TWO CHANGES, BOTH ABOUT NOT LYING TO THE SHOPPER ─────
 *
 * 1. THE MUTATION NOW GOES THROUGH `updateCartLines`, not `actions.updateCart` raw.
 *    `assets/cart-actions.js` calls itself "the one place this theme mutates the
 *    cart" and this listener was the one call site that went around it. Going
 *    through it buys two things that cannot be had here:
 *      · `addRefusal` — a sold-out add comes back with an EMPTY `userErrors` and
 *        the refusal in `warnings`, so the `userErrors.length` test below was
 *        reporting SUCCESS for an add that never happened. The numbers are in
 *        `addRefusal`'s doc block. `updateCartLines` promotes the refusal into
 *        `userErrors`, so the `showAddError` branch below now actually fires.
 *      · `origin: el` — the emitted event's `target` becomes THIS FORM. It was
 *        `theme-drawer` before, because a raw `updateCart` sets no mutation origin
 *        and `standard-actions-override.js` falls back to the drawer scope.
 *
 *    ⚠️ THIS FILE IS NO LONGER IMPORT-FREE, and the ARCHITECTURE note at the top of
 *    it used to say it was. Corrected there too. The import costs no new bytes on
 *    any page that renders cards: `@theme/cart-actions` is already in the graph
 *    from `component-cart-items.js`, which `snippets/cart-products.liquid` loads for
 *    the drawer in `layout/theme.liquid`. VERIFIED on a rendered collection page —
 *    `performance.getEntriesByType('resource')` lists `cart-actions.js` before this
 *    change was made.
 *
 * 2. IT NO LONGER OPENS THE CART ITSELF. `assets/rp-cart-feedback.js` opens the
 *    drawer for EVERY successful add, from every path, off the one event. This
 *    listener calling `openCart()` as well is how the card and the PDP came to
 *    disagree in the first place — the card confirmed, the PDP did not — so the
 *    call is removed rather than duplicated.
 */
document.addEventListener('submit', (event) => {
  const el = event.target;
  if (!(el instanceof HTMLFormElement) || el.dataset.rpAdd === undefined) return;

  const actions = window.Shopify?.actions;
  const button = event.submitter;
  if (!actions?.updateCart || !(button instanceof HTMLElement) || !button.getAttribute('value')) {
    return; // native POST
  }

  event.preventDefault();
  const error = el.querySelector('[data-rp-add-error]');
  resetAddError(error);
  button.setAttribute('aria-disabled', 'true');

  Promise.resolve()
    .then(() =>
      updateCartLines([{ merchandiseId: button.getAttribute('value'), quantity: 1 }], {
        action: 'add',
        context: 'product',
        origin: el,
      })
    )
    .then((result) => {
      button.removeAttribute('aria-disabled');
      // The action ran and the cart refused it — a real, reportable outcome.
      // Reachable since `updateCartLines` started promoting a sold-out refusal out of
      // `warnings` and into `userErrors`; before that this branch was dead for the
      // commonest failure there is.
      if (result && Array.isArray(result.userErrors) && result.userErrors.length) {
        // `translated` is set only by `addRefusal`, whose copy comes from
        // `Theme.translations`. Every other userError may carry a raw English platform
        // string — the AJAX fallback's `ajaxUserErrors` passes those straight through —
        // so those keep the region's own translated copy.
        const first = result.userErrors[0];
        showAddError(error, first?.translated ? first.message : undefined);
        return;
      }
      // The drawer is opened by `assets/rp-cart-feedback.js`, off the same event —
      // one place, so this path and the PDP cannot diverge again.
    })
    .catch(() => {
      // The action itself failed. Hand the mutation back to the browser rather
      // than leaving the shopper with a dead control.
      //
      // ⚠️ el.submit() serialises the form's OWN fields only. The size the shopper
      // picked lives on the button, not in the form, so a bare submit posted the
      // product's DEFAULT variant — silently adding the wrong size to the cart.
      // Caught in adversarial review 2026-09-03. Carry the id explicitly.
      button.removeAttribute('aria-disabled');
      const variantId = button.dataset.variantId || button.value;
      if (variantId) {
        let field = el.querySelector('input[name="id"]');
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'id';
          el.appendChild(field);
        }
        field.value = variantId;
      }
      el.submit();
    });
});

/* 044 §4 rule 5 — "Pause when off-screen and on `visibilitychange`. A <video>
 * that keeps decoding in a background tab is the classic battery bug and it is
 * invisible in testing." Nothing in the theme implemented it: media.js registers
 * exactly two pause triggers (mediaStartedPlaying at media.js:23 and
 * DialogCloseEvent at media.js:24) and neither is the tab going away.
 *
 * PAUSE, not stopVideo(). Teardown drops the source and removes the element, so
 * a shopper who switched tabs mid-clip would come back to the poster and lose
 * their position; the spec asks for pause and the native controls resume it.
 * The clip is muted either way, so this is a decoder and battery fix, not an
 * audio one.
 *
 * A lifecycle listener, not a delegated one — there is nothing to delegate. It
 * is neither a scroll nor a resize listener, so it is outside 060 §6.3's caps,
 * and slideshow.js:559, announcement-bar.js:29 and live-clock.js:169 already
 * establish `visibilitychange` as the theme's idiom for this. */
document.addEventListener('visibilitychange', () => {
  if (!document.hidden || !openVideo) return;
  const video = openVideo.querySelector('video');
  if (!video || video.paused) return;
  // Marked quiet: backgrounding a tab is not the shopper pausing the clip, and
  // without the flag the latch would record it as one and disable autoplay for
  // that card for the rest of the page's life.
  openVideo.dataset.rpQuiet = '1';
  video.pause();
});

/* ── 036b · AUTOPLAY, and why it is one video ─────────────────────────────
 * Client, 2026-09-09: *"Video should auto play, muted. Change that as a
 * decision."* Full record and reasoning: `docs/decisions/008-video-autoplay.md`.
 *
 * The hard part is not starting playback, it is that a collection page has 40
 * cards and video is COMMON in this catalogue — dozens of products carry one,
 * not the two an earlier version of this comment claimed (measured 2026-09-09
 * against the Admin API; ADR-008 lists the numbers). The two clips that were
 * actually measured are 32.8 MB / 16.3 s and 22.8 MB / 11.5 s, both square
 * 1080×1080. Forty of those is not a feature, it is an outage.
 *
 * So autoplay reuses the SINGLE-VIDEO INVARIANT this module already has:
 * `playVideo` displaces any other open host, `openVideo` is one host, and
 * `stopVideo` drops the source and calls `load()` so a detached node cannot sit
 * on buffered data. At most one video is mounted on the page at any moment,
 * autoplaying or not.
 *
 * WHAT TRIGGERS IT — and what does NOT. When the video's own slide is the one
 * being shown and the card is in the viewport, it plays. Not "every card with a
 * video plays as you scroll": neither artboard asks for that, and it is the
 * outage above.
 *
 * THE MERCHANDISED ORDER IS NOT TOUCHED. `docs/canvas/Collection.dc.html` draws
 * the hovered card with its FIRST dot current, so hover reveals the stepper and
 * does not advance it; promoting the clip or jumping to it on hover would both
 * contradict that, and the merchant's own order is not uniform anyway (the clip
 * is last on most products but sits at 10 of 11 on `wide-pant-medium-blue` and
 * `track-jacket-color-block`, and 4 of 5 on `dungaree-dress-medium-blue`).
 * ADR-008 §4 records this as a decision rather than leaving it implicit.
 *
 * A shopper who pauses it stays paused. `data-rp-user-paused` latches on the
 * GALLERY element, not on the slide: `release()` clears the slide container with
 * `replaceChildren()`, so a latch stored on the slide dies the moment the card
 * scrolls out and the clip autoplays again on the way back. Measured before the
 * move — pause → latch '1'; scroll to bottom → latch null; scroll back → playing
 * at t=3.95. See the `pause` listener in `playVideo` for why the latch keys on
 * the media event behind a quiet-flag rather than on a click: the player renders
 * native controls, so a pause from the native control never reaches a click
 * listener at all.
 *
 * MOTION. Autoplay is motion. Under `prefers-reduced-motion: reduce` or
 * `html[data-motion="off"]` nothing mounts and nothing is fetched — the poster
 * and the play button remain, exactly as before this block existed. */

const motionOff = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ||
  document.documentElement.dataset.motion === 'off';

/** The slide currently shown carries the video, and the card is on screen. */
function autoplayIfShown(gal) {
  /* Pointer devices only. Where the gallery swipes there is no hover, so there
   * is no engagement signal to key off — and `docs/canvas/Mobile.dc.html` gives
   * the mobile card no gallery affordance at all, just one still. Starting a
   * 22–33 MB square master because a card scrolled past on a phone is the thing
   * every constraint in ADR-008 exists to prevent. Nothing mounts, nothing is
   * fetched; the poster and the play control remain and still work. */
  if (!layered) return;
  if (motionOff() || document.hidden) return;

  const slide = gal.querySelector('.rp-card__slide[data-rp-video]');
  if (!slide) return;

  /* ⛔ THIS INDEX WAS OFF BY ONE, AND THAT — NOT THE SLIDE ORDER — IS WHY THE
   * CLIENT SAW NO AUTOPLAY AFTER AUTOPLAY WAS ADDED.
   *
   * `show()` treats index 0 as "the first frame, no layer showing" and activates
   * `slides[next - 1]` for every index above it. The old line read
   * `slides[Number(gal.dataset.index || 0)]`, one place too far along. So on
   * `bomber-coat-pure-chocolate` (2 deferred images + the video = 3 slides, the
   * video at array index 2, displayed at index 3) the test passed at index 2 —
   * mounting and playing the clip UNDERNEATH the second image, invisibly — and
   * failed at index 3, where `slides[3]` is undefined, so the `!isShown` branch
   * ran `stopVideo()` at the exact moment the video became visible.
   *
   * Playing where it cannot be seen, stopping where it can. Reading the code is
   * enough to see it; it needs no device to reproduce. */
  const slides = gal.querySelectorAll('[data-rp-slides] .rp-card__slide');
  const index = Number(gal.dataset.index || 0);
  const isShown = index > 0 && slides[index - 1] === slide;

  if (!isShown) {
    if (openVideo === slide) stopVideo();
    return;
  }
  if (gal.dataset.rpUserPaused !== undefined) return;
  if (openVideo === slide) return;
  const button = slide.querySelector('[data-rp-play]');
  if (button) playVideo(button);
}

/* ── the page's one card observer ────────────────────────────────────────── */

const io = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      const gal = entry.target;
      if (entry.isIntersecting) {
        // Where the gallery swipes there is no hover to wait for, so the track
        // is built as the card approaches. The slides are `loading="lazy"`, but
        // they sit BESIDE slide 1 and Chrome's lazy margin is on both axes, so
        // they load with the card (032 §4) — accepted, see u8-product-card.css.
        if (!layered) hydrate(gal);
        autoplayIfShown(gal);
      } else {
        // Stop FIRST, then release. `release` deliberately bails out while the
        // gallery holds the open video (`if (openVideo && gal.contains(openVideo))
        // return`) so that a click-to-play video survives a scroll — correct for
        // a video a shopper started, wrong for one that started itself. Without
        // this line an autoplayed card scrolled off screen keeps decoding.
        if (openVideo && gal.contains(openVideo)) stopVideo();
        release(gal);
      }
    }
  },
  // One viewport of margin: hydrate just before arrival, release once a full
  // screen past. 032 §9.4 flags its own "two screens" as a guess to verify on
  // a real device after a long scroll session.
  { rootMargin: '100% 0px' }
);

/**
 * One job now: put galleries under the observer. The second job — upgrading a
 * size trigger from "link to the PDP" to "disclosure button" — went with the
 * trigger itself when the card was rebuilt from the canvas.
 */
function observe(root) {
  if (root.matches?.('[data-rp-gallery]')) io.observe(root);
  root.querySelectorAll?.('[data-rp-gallery]').forEach((gal) => io.observe(gal));
}

observe(document);

/**
 * Cards also arrive from the Section Rendering API — facets, pagination and
 * sort all morph a fresh grid in, and Horizon exposes no section-refresh event
 * to hook (`assets/events.js` declares four events; none of them is one). So
 * this is the only way a re-rendered grid gets its gallery on the surface where
 * the gallery is not hover-triggered. ONE observer for the page, childList
 * only, and the callback does nothing unless an element was added.
 */
new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType === 1) observe(node);
    }
  }
}).observe(document.body, { childList: true, subtree: true });
